import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  TableContainer,
  Paper,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,

  FormControl,
  Box,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  Avatar,
  Chip,
  Switch,
  Menu,
  Snackbar,
  Alert,
  FormControlLabel,
} from "@mui/material";
import { Search, Edit, Trash2, ShoppingBag, ArrowUp, ArrowDown, GripVertical, X, Settings } from 'lucide-react';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import ProductTools from "../components/ProductTools"
import api from '../utils/api';
import currencyService from '../services/currencyService';
import {getProfit, getMargin, getROI, getTVADetails } from '../utils/calculs';
import DialogRepricer from '../components/DialogRepricer';
import useDebounce from '../utils/useDebounce';
import AchatsLies from '../components/AchatsLies';
import CommandesLiees from '../components/CommandesLiees';
import StatistiquesProduit from '../components/StatistiquesProduit';
import RepricerProduit from '../components/RepricerProduit';
import ImageInfosProduit from '../components/ImageInfosProduit';
import Produit from '../components/Produit';

// --- UTILS CACHE RESTRICTIONS AMAZON ---
const RESTRICTIONS_CACHE_KEY = 'amazon_restrictions_cache_v1';
const RESTRICTIONS_CACHE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

function getRestrictionsCache() {
  try {
    const raw = localStorage.getItem(RESTRICTIONS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || !parsed) return {};
    return parsed;
  } catch {
    return {};
  }
}

function setRestrictionsCache(cache) {
  try {
    localStorage.setItem(RESTRICTIONS_CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

function getCachedRestriction(asin, marketplaceId) {
  const cache = getRestrictionsCache();
  const key = `${asin}_${marketplaceId}`;
  if (!cache.hasOwnProperty(key)) return null; // Correction : on vérifie la présence de la clé
  const entry = cache[key];
  if (Date.now() > entry.expireAt) {
    // Expiré
    delete cache[key];
    setRestrictionsCache(cache);
    return null;
  }
  return entry.data; // Peut être null, c'est OK
}


function setBulkCachedRestrictions(bulkData) {
  // bulkData = { [asin]: { [marketplaceId]: restrictionObj } }
  const cache = getRestrictionsCache();
  Object.entries(bulkData).forEach(([asin, mpMap]) => {
    Object.entries(mpMap).forEach(([marketplaceId, data]) => {
      const key = `${asin}_${marketplaceId}`;
      cache[key] = {
        data,
        expireAt: Date.now() + RESTRICTIONS_CACHE_DURATION_MS
      };
    });
  });
  setRestrictionsCache(cache);
}

// Supprime toutes les restrictions du cache pour un ASIN donné
function purgeRestrictionsCacheForAsin(asin) {
  const cache = getRestrictionsCache();
  let changed = false;
  Object.keys(cache).forEach(key => {
    if (key.startsWith(`${asin}_`)) {
      delete cache[key];
      changed = true;
    }
  });
  if (changed) setRestrictionsCache(cache);
}
// --- FIN UTILS CACHE ---

// --- UTILS DEEP EQUAL POUR LE CACHE RESTRICTIONS ---
function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object' || a === null || b === null) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (let key of aKeys) {
    if (!b.hasOwnProperty(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }
  return true;
}


// Formatage de date au format dd-MM-yyyy
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const MARKETPLACES = [
  { id: 'A13V1IB3VIYZZH', code: 'FR', label: 'France', flag: 'https://flagcdn.com/24x18/fr.png', currency: '€', currencyCode: 'EUR' },
  { id: 'APJ6JRA9NG5V4', code: 'IT', label: 'Italie', flag: 'https://flagcdn.com/24x18/it.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A1RKKUPIHCS9HS', code: 'ES', label: 'Espagne', flag: 'https://flagcdn.com/24x18/es.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A1805IZSGTT6HS', code: 'NL', label: 'Pays-Bas', flag: 'https://flagcdn.com/24x18/nl.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A2NODRKZP88ZB9', code: 'SE', label: 'Suède', flag: 'https://flagcdn.com/24x18/se.png', currency: 'kr', currencyCode: 'SEK' },
  { id: 'A1C3SOZRARQ6R3', code: 'PL', label: 'Pologne', flag: 'https://flagcdn.com/24x18/pl.png', currency: 'zł', currencyCode: 'PLN' },
  { id: 'AMEN7PMS3EDWL', code: 'BE', label: 'Belgique', flag: 'https://flagcdn.com/24x18/be.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A1PA6795UKMFR9', code: 'DE', label: 'Allemagne', flag: 'https://flagcdn.com/24x18/de.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A1F83G8C2ARO7P', code: 'UK', label: 'Royaume-Uni', flag: 'https://flagcdn.com/24x18/gb.png', currency: '£', currencyCode: 'GBP' },
];

// Mapping des taux de TVA par pays
const TVA_BY_COUNTRY = {
  FR: 20,
  DE: 19,
  IT: 22,
  ES: 21,
  UK: 20,
  NL: 21,
  BE: 21,
  SE: 25,
  PL: 23,
  IE: 23
};



// --- COULEURS SEUILS ---
function getColorForValue(value, thresholds) {
  if (value === null || value === undefined) return { bg: 'inherit', text: 'inherit' };
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return { bg: 'inherit', text: 'inherit' };
  if (numValue >= thresholds.high) return { bg: '#e8f5e9', text: '#2e7d32' };
  if (numValue >= thresholds.medium) return { bg: '#fff3e0', text: '#ef6c00' };
  return { bg: '#ffebee', text: '#c62828' };
}
// ... existing code ...

const ProduitsPage = () => {
  // Déclaration de tous les hooks d'état en premier
  const [refreshRestrictionsTrigger, setRefreshRestrictionsTrigger] = useState(0);
  const [openConfirmRefresh, setOpenConfirmRefresh] = useState(false);
  const [asinToRefresh, setAsinToRefresh] = useState(null);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([{ id: "Titre", desc: false }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    Titre: "",
    EAN: "",
    ASIN: "",
    "Prix de vente moyen": "",
    Stock: "",
    Actif: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Etats pour les commandes liées à un produit
  const [openOrdersDialog, setOpenOrdersDialog] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedProductForOrders, setSelectedProductForOrders] = useState(null);

  // Nouveaux états pour le redimensionnement et l'ordre des colonnes
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('produitsColumnOrder');
    return savedOrder ? JSON.parse(savedOrder) : [];
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizing = localStorage.getItem('produitsColumnSizing');
    if (savedSizing) {
      return JSON.parse(savedSizing);
    }
    // Valeurs par défaut
    return {
      Titre: 250,
      EAN: 150,
      ASIN: 150,
      Stock: 100,
      "Prix de vente moyen": 150,
      commandes: 100,
      tools: 100,
      actions: 120,
    };
  });
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  
  // État pour la sélection et le survol des lignes
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  
  // État pour les filtres de colonnes
  const [filters, setFilters] = useState({
    Stock: { operator: '>=', value: '' },
    "Prix de vente moyen": { operator: '>=', value: '' },
    Actif: { value: '' },
    Titre: { value: '' },
    EAN: { value: '' },
    ASIN: { value: '' },
  });

  // Mettre le mode moderne par défaut
  const [viewMode, setViewMode] = useState('moderne');
  const [openRepricerModal, setOpenRepricerModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rules, setRules] = useState([]);
  const [modernPage, setModernPage] = useState(0);
  const MODERN_PAGE_SIZE = 10;
  const [repricingRuleData, setRepricingRuleData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actifFilter, setActifFilter] = useState(true);
  const [modernSearch, setModernSearch] = useState("");
  const [selectedAchatId, setSelectedAchatId] = useState('');

  // Ajout de l'état pour la personnalisation ouverte par marketplace
  const [openPersonnalisation, setOpenPersonnalisation] = useState({});

  // Dans la sous-ligne Minimums, on gère l'état local pour les champs et le calcul dynamique
  const [personnalisation, setPersonnalisation] = useState({});

  // Ajout d'un état pour stocker les achats du produit sélectionné
  const [achatsProduit, setAchatsProduit] = useState([]);
  const [achatsByProduct, setAchatsByProduct] = useState({});
  const [commandesByProduct, setCommandesByProduct] = useState({});
  const lastFetchedIds = useRef([]);

  // Ajout d'un hook pour stocker les taux de change réels (EUR <-> SEK, PLN, GBP)
  const [exchangeRates, setExchangeRates] = useState({});
  useEffect(() => {
    async function loadRates() {
      const rates = {};
      for (const currency of ['SEK', 'PLN', 'GBP']) {
        try {
          const rate = await currencyService.getExchangeRate(currency, 'EUR');
          rates[currency] = rate;
        } catch (e) {
          rates[currency] = null;
        }
      }
      setExchangeRates(rates);
    }
    loadRates();
  }, []);

  useEffect(() => {
    if (selectedProduct?.id) {
      fetch(`/api/achats?produit=${selectedProduct.id}`)
        .then(res => res.json())
        .then(data => setAchatsProduit(data))
        .catch(() => setAchatsProduit([]));
    }
  }, [selectedProduct]);

  const debouncedModernSearch = useDebounce(modernSearch, 400);

  const produitsFiltres = useMemo(() => {
    let arr = produits;
    if (actifFilter !== null) arr = arr.filter(p => !!p.Actif === actifFilter);
    if (debouncedModernSearch.trim()) {
      const search = debouncedModernSearch.trim().toLowerCase();
      arr = arr.filter(p =>
        (p.Titre || "").toLowerCase().includes(search) ||
        (p.EAN || "").toLowerCase().includes(search) ||
        (p.ASIN || "").toLowerCase().includes(search)
      );
    }
    return arr;
  }, [produits, actifFilter, modernSearch]);

  const totalModernPages = Math.ceil(produitsFiltres.length / MODERN_PAGE_SIZE);
  const produitsPage = produitsFiltres.slice(modernPage * MODERN_PAGE_SIZE, (modernPage + 1) * MODERN_PAGE_SIZE);

  const fetchProduits = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/produits");
      let data = Array.isArray(res.data) ? res.data : [];
      data = data.sort((a, b) => (a.Titre || '').localeCompare(b.Titre || '', 'fr', { sensitivity: 'base' }));
      setProduits(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduits();
  }, []);

  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('produitsColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem('produitsColumnSizing', JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  useEffect(() => {
    // Récupère la liste des règles à l'ouverture de la modale repricer
    if (openRepricerModal) {
      fetch('/api/rules')
        .then(res => res.json())
        .then(data => setRules(data))
        .catch(() => setRules([]));
    }
  }, [openRepricerModal]);

  // Pré-remplissage de l'état personnalisation à l'ouverture de la modale repricer
  useEffect(() => {
    if (openRepricerModal && selectedProduct && repricingRuleData[selectedProduct.id]) {
      const rulesForProduct = repricingRuleData[selectedProduct.id];
      const newPersonnalisation = {};
      MARKETPLACES.forEach(mp => {
        const rule = rulesForProduct[mp.code];
        if (rule) {
          // Min
          if (rule.min_profit_override !== null && rule.min_profit_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), minType: 'profit', minValue: rule.min_profit_override };
          } else if (rule.min_margin_override !== null && rule.min_margin_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), minType: 'marge', minValue: rule.min_margin_override };
          } else if (rule.min_roi_override !== null && rule.min_roi_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), minType: 'roi', minValue: rule.min_roi_override };
          }
          // Max
          if (rule.max_profit_override !== null && rule.max_profit_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), maxType: 'profit', maxValue: rule.max_profit_override };
          } else if (rule.max_margin_override !== null && rule.max_margin_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), maxType: 'marge', maxValue: rule.max_margin_override };
          } else if (rule.max_roi_override !== null && rule.max_roi_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), maxType: 'roi', maxValue: rule.max_roi_override };
          }
        }
      });
      setPersonnalisation(newPersonnalisation);
    }
    // eslint-disable-next-line
  }, [openRepricerModal, selectedProduct, repricingRuleData]);

  // Lors de l'ouverture de la modale repricer, ou lors du mapping des marketplaces dans le tableau, ouvrir la personnalisation si un override est présent :
  useEffect(() => {
    if (openRepricerModal && selectedProduct && personnalisation) {
      const newOpenPersonnalisation = {};
      MARKETPLACES.forEach(mp => {
        const pers = personnalisation[mp.code] || {};
        if (
          (pers.minValue && pers.minValue !== '') ||
          (pers.maxValue && pers.maxValue !== '') ||
          (pers.minType && pers.minType !== '') ||
          (pers.maxType && pers.maxType !== '')
        ) {
          newOpenPersonnalisation[mp.code] = true;
        }
      });
      setOpenPersonnalisation(prev => ({ ...prev, ...newOpenPersonnalisation }));
    }
  }, [openRepricerModal, selectedProduct, personnalisation]);

  const handleOpenDialog = () => {
    setFormData({
      Titre: "",
      EAN: "",
      ASIN: "",
      "Prix de vente moyen": "",
      Stock: "",
      Actif: true,
    });
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await api.put(`/api/produits/${editingId}`, formData);
      } else {
        await api.post("/api/produits", formData);
      }
      fetchProduits();
      setOpenDialog(false);
      setEditingId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (produit) => {
    setFormData({
      Titre: produit.Titre,
      EAN: produit.EAN,
      ASIN: produit.ASIN,
      "Prix de vente moyen": produit["Prix de vente moyen"],
      Stock: produit.Stock,
      Actif: produit.Actif,
    });
    setEditingId(produit.id);
    setOpenDialog(true);
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/produits/${deleteId}`);
      fetchProduits();
      setOpenDeleteDialog(false);
      setDeleteId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  const handleOpenOrdersDialog = async (produit) => {
    setSelectedProductForOrders(produit);
    try {
      const res = await fetchProductOrders(produit);
      const filteredOrders = Array.isArray(res) ? res : [];
      setOrders(filteredOrders);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des commandes pour ce produit:",
        error
      );
      setOrders([]);
    }
    setOpenOrdersDialog(true);
  };

  const handleCloseOrdersDialog = () => {
    setOpenOrdersDialog(false);
    setOrders([]);
    setSelectedProductForOrders(null);
  };

  const fetchProductOrders = async (produit) => {
    try {
      const response = await api.get(`/api/commandes?produit=${produit.id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
      return [];
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.accessor("Titre", {
        header: "Titre",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.Titre,
        enableResizing: true,
        filterFn: 'text',
      }),
      columnHelper.accessor("EAN", {
        header: "EAN",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.EAN,
        enableResizing: true,
        filterFn: 'text',
      }),
      columnHelper.accessor("ASIN", {
        header: "ASIN",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.ASIN,
        enableResizing: true,
        filterFn: 'text',
      }),
      columnHelper.accessor("Stock", {
        header: "Stock",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.Stock,
        enableResizing: true,
        filterFn: 'custom',
      }),
      columnHelper.accessor("Prix de vente moyen", {
        header: "Prix de vente moyen",
        cell: (info) => {
          const value = Number(info.getValue()) || 0;
          return `${value.toFixed(2)} €`;
        },
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing["Prix de vente moyen"],
        enableResizing: true,
        filterFn: 'custom',
      }),
      columnHelper.display({
        id: "buybox",
        header: () => (
          <Tooltip title="Vous détenez la Buybox Amazon ?" arrow>
            <span>Buybox ?</span>
          </Tooltip>
        ),
        cell: (info) => {
          const produit = info.row.original;
          const rulesForProduct = repricingRuleData[produit.id] || {};
          let rule = rulesForProduct['FR'];
          if (!rule) {
            const allRules = Object.values(rulesForProduct);
            if (allRules.length > 0) rule = allRules[0];
          }
          const getBB = rule?.getBB;
          return getBB ? (
            <Tooltip title="Vous détenez la Buybox" arrow>
              <Chip label="Oui" color="success" size="small" icon={<span style={{fontSize:16}}>🏆</span>} />
            </Tooltip>
          ) : (
            <Tooltip title="Buybox non détenue" arrow>
              <Chip label="Non" color="default" size="small" />
            </Tooltip>
          );
        },
        size: 90,
        enableResizing: true,
      }),
      columnHelper.display({
        id: "position",
        header: () => (
          <Tooltip title="Votre position parmi les offres Amazon" arrow>
            <span>Position</span>
          </Tooltip>
        ),
        cell: (info) => {
          const produit = info.row.original;
          const rulesForProduct = repricingRuleData[produit.id] || {};
          let rule = rulesForProduct['FR'];
          if (!rule) {
            const allRules = Object.values(rulesForProduct);
            if (allRules.length > 0) rule = allRules[0];
          }
          const pos = rule?.Position;
          return pos !== undefined && pos !== null ? (
            <Tooltip title={`Position : ${pos}`} arrow>
              <Chip label={pos} color={pos === 1 ? 'success' : 'info'} size="small" />
            </Tooltip>
          ) : '-';
        },
        size: 80,
        enableResizing: true,
      }),
      columnHelper.display({
        id: "bb_price",
        header: () => (
          <Tooltip title="Prix actuel de la Buybox" arrow>
            <span>Prix Buybox</span>
          </Tooltip>
        ),
        cell: (info) => {
          const produit = info.row.original;
          const rulesForProduct = repricingRuleData[produit.id] || {};
          let rule = rulesForProduct['FR'];
          if (!rule) {
            const allRules = Object.values(rulesForProduct);
            if (allRules.length > 0) rule = allRules[0];
          }
          const bb = rule?.BB;
          return bb !== undefined && bb !== null ? (
            <Tooltip title="Prix Buybox" arrow>
              <Chip label={`${Number(bb).toFixed(2)} €`} color="warning" size="small" />
            </Tooltip>
          ) : '-';
        },
        size: 110,
        enableResizing: true,
      }),
      columnHelper.accessor("Actif", {
        header: "Actif",
        cell: (info) => info.getValue() ? "Oui" : "Non",
        enableColumnFilter: true,
        enableSorting: true,
        size: 80,
        enableResizing: true,
        filterFn: 'actif',
      }),
      columnHelper.display({
        id: "commandes",
        header: "Commandes",
        cell: (info) => (
          <IconButton
            size="small"
            onClick={() => handleOpenOrdersDialog(info.row.original)}
            title="Voir commandes"
          >
            <ShoppingBag size={20} />
          </IconButton>
        ),
        size: columnSizing.commandes,
        enableResizing: true,
      }),
      columnHelper.display({
        id: "tools",
        header: "Outils",
        cell: (info) => {
          const produit = info.row.original;
          return produit.ASIN ? (
            <ProductTools asin={produit.ASIN} />
          ) : (
            "-"
          );
        },
        size: columnSizing.tools,
        enableResizing: true,
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <div style={{ display: "flex", gap: "4px" }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(info.row.original)}
              title="Éditer"
            >
              <Edit size={20} />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleOpenDeleteDialog(info.row.original.id)}
              title="Supprimer"
            >
              <Trash2 size={20} />
            </IconButton>
          </div>
        ),
        size: columnSizing.actions,
        enableResizing: true,
      }),
    ],
    [columnSizing]
  );

  useEffect(() => {
    if (columns.length > 0 && columnOrder.length === 0) {
      const initialOrder = columns.map(col => col.id);
      setColumnOrder(initialOrder);
    }
  }, [columns, columnOrder]);

  // Puis seulement ensuite, la déclaration de table
  const table = useReactTable({
    data: produits,
    columns,
    state: { 
      globalFilter, 
      pagination, 
      sorting,
      columnOrder,
      columnSizing,
      columnFilters: Object.entries(filters).map(([id, filter]) => {
        return {
          id,
          value: filter
        };
      })
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnOrderChange: (newOrder) => {
      setColumnOrder(newOrder);
    },
    onColumnSizingChange: (newSizing) => {
      setColumnSizing(newSizing);
      localStorage.setItem('produitsColumnSizing', JSON.stringify(newSizing));
    },
    onColumnFiltersChange: (updater) => {
      if (typeof updater === 'function') {
        const newFilters = updater(
          Object.entries(filters).map(([id, filter]) => ({
            id,
            value: filter
          }))
        );
        
        const updatedFilters = {};
        newFilters.forEach(filter => {
          updatedFilters[filter.id] = filter.value;
        });
        
        setFilters(updatedFilters);
      } else {
        const updatedFilters = {};
        updater.forEach(filter => {
          updatedFilters[filter.id] = filter.value;
        });
        
        setFilters(updatedFilters);
      }
    },
    globalFilterFn: "includesString",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    enableColumnOrdering: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
      size: 150,
    },
    filterFns: {
      custom: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        
        let value = parseFloat(row.getValue(columnId));
        
        // Pour le Stock et le Prix de vente moyen
        if ((columnId === 'Stock' || columnId === 'Prix de vente moyen')) {
          // Si la valeur est nulle, vide, undefined ou égale à 0
          if (value === null || value === undefined || isNaN(value) || value === 0) {
            // Si l'opérateur est '=', on retourne true UNIQUEMENT si la valeur recherchée est 0
            if (filterValue.operator === '=' && parseFloat(filterValue.value) === 0) {
              return true;
            }
            // Pour les autres opérateurs, on compare avec 0
            value = 0;
          }
        } else if (isNaN(value)) {
          return true;
        }

        const filterValueNum = parseFloat(filterValue.value);
        if (isNaN(filterValueNum)) return true;

        switch (filterValue.operator) {
          case '>=':
            return value >= filterValueNum;
          case '>':
            return value > filterValueNum;
          case '<=':
            return value <= filterValueNum;
          case '<':
            return value < filterValueNum;
          case '=':
            // Pour l'opérateur '=', on compare exactement
            return Math.abs(value - filterValueNum) < 0.0001; // Utiliser une petite marge d'erreur pour les nombres à virgule
          default:
            return true;
        }
      },
      equals: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        
        const value = row.getValue(columnId);
        return value === filterValue.value;
      },
      text: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        const value = (row.getValue(columnId) || '').toString().toLowerCase();
        return value.includes(filterValue.value.toLowerCase());
      },
      actif: (row, columnId, filterValue) => {
        if (!filterValue || filterValue.value === '') return true;
        const value = row.getValue(columnId);
        return (filterValue.value === 'true' && value === true) ||
               (filterValue.value === 'false' && value === false);
      },
    },
    autoResetPageIndex: false,
  });

  const pageCount = table.getPageCount() || 0;

  useEffect(() => {
    const productIds = produitsPage.map(p => p.id);
    // Compare avec la dernière liste fetchée
    if (
      produitsPage.length > 0 &&
      (lastFetchedIds.current.length !== productIds.length ||
       !lastFetchedIds.current.every((id, i) => id === productIds[i]))
    ) {
      lastFetchedIds.current = productIds;
      api.post('/api/repricing-rules/by-products', { product_ids: productIds })
        .then(response => {
          const rulesArr = response.data;
          setRepricingRuleData(prev => {
            const newData = { ...prev };
            rulesArr.forEach(rule => {
              if (!newData[rule.product_id]) newData[rule.product_id] = {};
              newData[rule.product_id][rule.marketplace] = rule;
            });
            return newData;
          });
        });
    }
  }, [produitsPage]);

  // Chargement des règles au montage
  useEffect(() => {
    api.get('/api/rules')
      .then(res => setRules(res.data))
      .catch(() => setRules([]));
  }, []);

  useEffect(() => {
    const productIds = produitsPage.map(p => p.id);
    let cancelled = false;

    // Achats
    const fetchAchatsForCurrentPage = async () => {
      let achatsByProduct = {};
      if (productIds.length > 0) {
        try {
          const resAchats = await api.post('/api/achats/by-products', { product_ids: productIds });
          achatsByProduct = resAchats.data || {};
        } catch (e) {
          achatsByProduct = {};
        }
      }
      if (!cancelled) setAchatsByProduct(achatsByProduct);
    };

    // Commandes
    const fetchCommandesForCurrentPage = async () => {
      let commandesByProduct = {};
      if (productIds.length > 0) {
        try {
          const resCommandes = await api.post('/api/commandes/by-products', { product_ids: productIds });
          commandesByProduct = resCommandes.data || {};
        } catch (e) {
          commandesByProduct = {};
        }
      }
      if (!cancelled) setCommandesByProduct(commandesByProduct);
    };

    fetchAchatsForCurrentPage();
    fetchCommandesForCurrentPage();

    return () => { cancelled = true; };
  }, [modernPage, MODERN_PAGE_SIZE, produits, actifFilter, modernSearch]);

  // Ajoute une fonction pour rafraîchir les règles d'un produit
  const refreshRepricingRules = async (productId) => {
    if (!productId) return;
    const response = await api.post('/api/repricing-rules/by-products', { product_ids: [productId] });
    const rulesArr = response.data;
    setRepricingRuleData(prev => {
      const newData = { ...prev };
      rulesArr.forEach(rule => {
        if (!newData[rule.product_id]) newData[rule.product_id] = {};
        newData[rule.product_id][rule.marketplace] = rule;
      });
      return newData;
    });
  };

  // État pour les restrictions Amazon par produit/marketplace
  const [restrictionsByProduct, setRestrictionsByProduct] = useState({});

  // Récupération des restrictions pour les produits affichés (page courante) - version bulk + cache localStorage
  useEffect(() => {
    const produitsPageASINs = produitsPage
      .filter(p => p.ASIN)
      .map(p => ({ id: p.id, asin: p.ASIN }));
    if (produitsPageASINs.length === 0) return;
    const asins = produitsPageASINs.map(p => p.asin);
    const marketplaceIds = MARKETPLACES.map(mp => mp.id);
    // On regarde d'abord dans le cache localStorage
    const cache = getRestrictionsCache();
    const asinToId = Object.fromEntries(produitsPageASINs.map(p => [p.asin, p.id]));
    // On prépare la structure restrictionsByProduct à partir du cache
    const restrictionsFromCache = {};
    // Liste des couples manquants
    const missingCouples = [];
    asins.forEach(asin => {
      const id = asinToId[asin];
      if (!id) return;
      restrictionsFromCache[id] = restrictionsFromCache[id] || {};
      marketplaceIds.forEach(marketplaceId => {
        // Correction : on teste la présence de la clé dans le cache, pas la valeur
        const cache = getRestrictionsCache();
        const key = `${asin}_${marketplaceId}`;
        if (cache.hasOwnProperty(key)) {
          const cached = getCachedRestriction(asin, marketplaceId);
          restrictionsFromCache[id][marketplaceId] = cached;
        } else {
          missingCouples.push({ asin, marketplaceId });
        }
      });
    });
    if (missingCouples.length === 0) {
      // Tout est en cache, on set et on sort
      setRestrictionsByProduct(prev => {
        if (deepEqual(prev, restrictionsFromCache)) return prev;
        return { ...prev, ...restrictionsFromCache };
      });
      return;
    }
    // Sinon, on fait l'appel API pour les couples manquants
    // Regroupe les asins à fetcher et les marketplaces concernées
    const asinsToFetch = [...new Set(missingCouples.map(c => c.asin))];
    const marketplacesToFetchByAsin = {};
    missingCouples.forEach(({ asin, marketplaceId }) => {
      if (!marketplacesToFetchByAsin[asin]) marketplacesToFetchByAsin[asin] = [];
      marketplacesToFetchByAsin[asin].push(marketplaceId);
    });
    // Appel API modifié : on passe la map asins -> marketplaces à fetcher
    api.getRestrictionsBulk({
      asins: asinsToFetch,
      sellerId: process.env.REACT_APP_AMAZON_SELLER_ID,
      marketplaceIds, // L'API peut être adaptée pour accepter marketplacesToFetchByAsin si besoin
    })
      .then(data => {
        // data = { [asin]: { [marketplaceId]: restrictionObj } }
        // On met à jour le cache pour tous les couples demandés, même si la réponse ne contient rien
        const couplesToCache = {};
        asinsToFetch.forEach(asin => {
          couplesToCache[asin] = {};
          (marketplacesToFetchByAsin[asin] || []).forEach(marketplaceId => {
            // Si la réponse contient une restriction, on la prend, sinon on met null
            const restrictionObj = data[asin] && data[asin][marketplaceId] !== undefined ? data[asin][marketplaceId] : null;
            couplesToCache[asin][marketplaceId] = restrictionObj;
          });
        });
        setBulkCachedRestrictions(couplesToCache);
        // On fusionne cache + data API
        const newRestrictions = { ...restrictionsFromCache };
        Object.entries(couplesToCache).forEach(([asin, mpMap]) => {
          const id = asinToId[asin];
          if (!id) return;
          newRestrictions[id] = newRestrictions[id] || {};
          Object.entries(mpMap).forEach(([marketplaceId, restrictionObj]) => {
            newRestrictions[id][marketplaceId] = restrictionObj;
          });
        });
        setRestrictionsByProduct(prev => {
          if (deepEqual(prev, newRestrictions)) return prev;
          return { ...prev, ...newRestrictions };
        });
      })
      .catch(() => {
        // En cas d'erreur, on set quand même ce qu'on a en cache
        setRestrictionsByProduct(prev => {
          if (deepEqual(prev, restrictionsFromCache)) return prev;
          return { ...prev, ...restrictionsFromCache };
        });
      });
  }, [produitsPage, refreshRestrictionsTrigger]);

  // Seuils pour Marge, ROI, Profit (identique à CommandesPage.js)
  const defaultThresholds = {
    roi: { high: 50, medium: 30, enabled: true },
    profit: { high: 100, medium: 50, enabled: true },
    marge: { high: 30, medium: 15, enabled: true },
  };
  const [thresholds, setThresholds] = useState(() => {
    const saved = localStorage.getItem("commandesThresholds");
    return saved ? JSON.parse(saved) : defaultThresholds;
  });

  const [fournisseurs, setFournisseurs] = useState([]);
  useEffect(() => {
    api.get('/api/fournisseurs').then(res => setFournisseurs(res.data));
  }, []);

  const [repricingTrends, setRepricingTrends] = useState({});

  const allRuleIds = useMemo(() =>
    produitsPage.flatMap(
      produit => MARKETPLACES.map(mp => repricingRuleData[produit.id]?.[mp.code]?.id)
    ).filter(Boolean),
    [produitsPage, repricingRuleData, MARKETPLACES]
  );

  useEffect(() => {
    if (allRuleIds.length === 0) return;
    api.postBulkRepricingHistory(allRuleIds)
      .then(res => setRepricingTrends(res))
      .catch(() => setRepricingTrends({}));
    // eslint-disable-next-line
  }, [JSON.stringify(allRuleIds)]);

  return (
    <div style={{ padding: "20px" }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ flexGrow: 1 }}>
          Gestion des Produits
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
          sx={{ bgcolor: '#f5f7fa', borderRadius: 2 }}
        >
          <ToggleButton value="table" sx={{ fontWeight: 600 }}>Tableau</ToggleButton>
          <ToggleButton value="moderne" sx={{ fontWeight: 600 }}>Moderne</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {viewMode === 'table' ? (
        <>
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Button variant="contained" color="primary" onClick={handleOpenDialog}>
              {editingId ? "Éditer un Produit" : "Ajouter un Produit"}
            </Button>
            <TextField
              label="Recherche globale"
              variant="outlined"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>
          {loading ? (
            <CircularProgress />
          ) : produits.length === 0 ? (
            <Typography>Aucun produit trouvé.</Typography>
          ) : (
            <>
              <TableContainer component={Paper}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #ccc",
                              borderRight: "1px solid #eee",
                              textAlign: "left",
                              cursor: header.column.getCanSort() ? "pointer" : "default",
                              position: "relative",
                              width: header.getSize(),
                              minWidth: 50,
                              userSelect: "none",
                              fontSize: "0.875rem",
                            }}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                cursor: 'move',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                }
                              }}
                              draggable
                              onDragStart={(e) => {
                                setDraggedColumnId(header.column.id);
                                e.currentTarget.style.opacity = '0.5';
                              }}
                              onDragEnd={(e) => {
                                setDraggedColumnId(null);
                                e.currentTarget.style.opacity = '1';
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                if (draggedColumnId && draggedColumnId !== header.column.id) {
                                  const newOrder = table.getAllLeafColumns()
                                    .map(col => col.id)
                                    .filter(id => id !== draggedColumnId);
                                  const targetIndex = newOrder.indexOf(header.column.id);
                                  newOrder.splice(targetIndex, 0, draggedColumnId);
                                  table.setColumnOrder(newOrder);
                                }
                              }}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <GripVertical 
                                sx={{ 
                                  mr: 0.5, 
                                  opacity: 0.2,
                                  fontSize: '1rem',
                                  '&:hover': { opacity: 0.5 }
                                }}
                              />
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <ArrowUp size={16} style={{ marginLeft: 4 }} />,
                                desc: <ArrowDown size={16} style={{ marginLeft: 4 }} />,
                              }[header.column.getIsSorted()] ?? null}
                            </Box>
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                width: '4px',
                                cursor: 'col-resize',
                                userSelect: 'none',
                                touchAction: 'none',
                                backgroundColor: '#e0e0e0',
                                opacity: 0.5,
                                transition: 'opacity 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.backgroundColor = '#2196f3';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '0.5';
                                e.currentTarget.style.backgroundColor = '#e0e0e0';
                              }}
                            />
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    <tr>
                      {table.getHeaderGroups()[0].headers.map((header) => {
                        const columnId = header.column.id;
                        const filter = filters[columnId];
                        
                        if (filter && (columnId === 'Stock' || columnId === 'Prix de vente moyen')) {
                          return (
                            <td
                              key={`filter-${columnId}`}
                              style={{
                                padding: "4px 8px",
                                borderBottom: "1px solid #eee",
                                borderRight: "1px solid #eee",
                                backgroundColor: "#f5f5f5",
                                fontSize: "0.875rem",
                                minHeight: "40px"
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}>
                                <FormControl size="small" sx={{ minWidth: 60 }}>
                                  <Select
                                    value={filter.operator}
                                    onChange={(e) => {
                                      const newFilter = { ...filter, operator: e.target.value };
                                      setFilters(prev => ({
                                        ...prev,
                                        [columnId]: newFilter
                                      }));
                                      header.column.setFilterValue(newFilter);
                                    }}
                                    size="small"
                                    sx={{ height: '36px' }}
                                  >
                                    <MenuItem value=">=">≥</MenuItem>
                                    <MenuItem value=">">&gt;</MenuItem>
                                    <MenuItem value="<=">≤</MenuItem>
                                    <MenuItem value="<">&lt;</MenuItem>
                                    <MenuItem value="=">=</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  value={filter.value}
                                  onChange={(e) => {
                                    const newFilter = { ...filter, value: e.target.value };
                                    setFilters(prev => ({
                                      ...prev,
                                      [columnId]: newFilter
                                    }));
                                    header.column.setFilterValue(newFilter);
                                  }}
                                  type="number"
                                  sx={{ 
                                    width: 60,
                                    '& .MuiInputBase-root': {
                                      height: '36px'
                                    }
                                  }}
                                />
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    // Réinitialiser complètement le filtre
                                    const newFilter = { operator: filter.operator, value: '' };
                                    setFilters(prev => ({
                                      ...prev,
                                      [columnId]: newFilter
                                    }));
                                    // Forcer la mise à jour du filtre dans la table
                                    header.column.setFilterValue(newFilter);
                                  }}
                                >
                                  <X size={16} />
                                </IconButton>
                              </Box>
                            </td>
                          );
                        }
                        if (columnId === 'Actif') {
                          return (
                            <td
                              key={`filter-${columnId}`}
                              style={{
                                padding: "4px 8px",
                                borderBottom: "1px solid #eee",
                                borderRight: "1px solid #eee",
                                backgroundColor: "#f5f5f5",
                                fontSize: "0.875rem",
                                minHeight: "40px"
                              }}
                            >
                              <FormControl size="small" sx={{ minWidth: 80 }}>
                                <Select
                                  value={filters.Actif.value}
                                  onChange={e => {
                                    const newFilter = { value: e.target.value };
                                    setFilters(prev => ({ ...prev, Actif: newFilter }));
                                    header.column.setFilterValue(newFilter);
                                  }}
                                  displayEmpty
                                  size="small"
                                >
                                  <MenuItem value="">Tous</MenuItem>
                                  <MenuItem value="true">Oui</MenuItem>
                                  <MenuItem value="false">Non</MenuItem>
                                </Select>
                              </FormControl>
                            </td>
                          );
                        }
                        if (["Titre", "EAN", "ASIN"].includes(columnId)) {
                          return (
                            <td
                              key={`filter-${columnId}`}
                              style={{
                                padding: "4px 8px",
                                borderBottom: "1px solid #eee",
                                borderRight: "1px solid #eee",
                                backgroundColor: "#f5f5f5",
                                fontSize: "0.875rem",
                                minHeight: "40px"
                              }}
                            >
                              <TextField
                                size="small"
                                value={filters[columnId]?.value || ''}
                                onChange={e => {
                                  const newFilter = { value: e.target.value };
                                  setFilters(prev => ({ ...prev, [columnId]: newFilter }));
                                  header.column.setFilterValue(newFilter);
                                }}
                                placeholder={`Filtrer...`}
                                sx={{ width: 120 }}
                              />
                            </td>
                          );
                        }
                        return (
                          <td
                            key={`filter-${columnId}`}
                            style={{
                              padding: "4px 8px",
                              borderBottom: "1px solid #eee",
                              borderRight: "1px solid #eee",
                              backgroundColor: "#f5f5f5",
                              fontSize: "0.875rem",
                              minHeight: "40px"
                            }}
                          />
                        );
                      })}
                    </tr>
                    {table.getRowModel().rows.map((row) => (
                      <tr 
                        key={row.id}
                        onClick={(e) => {
                          // Ne pas sélectionner la ligne si on clique sur un élément interactif
                          if (e.target.closest('button, a, [role="button"], .MuiIconButton-root')) {
                            return;
                          }
                          setSelectedRowId(row.id === selectedRowId ? null : row.id);
                        }}
                        onMouseEnter={() => setHoveredRowId(row.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        style={{
                          backgroundColor: row.id === selectedRowId 
                            ? 'rgba(33, 150, 243, 0.15)' 
                            : row.id === hoveredRowId 
                              ? 'rgba(0, 0, 0, 0.04)' 
                              : 'transparent',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #eee",
                              borderRight: "1px solid #eee",
                              width: cell.column.getSize(),
                              minWidth: 50,
                              maxWidth: cell.column.getSize(),
                              fontSize: "0.875rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              position: "relative"
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableContainer>
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: Math.max(prev.pageIndex - 1, 0),
                    }))
                  }
                  disabled={!table.getCanPreviousPage()}
                >
                  Précédent
                </Button>
                <span>
                  Page {pagination.pageIndex + 1} sur {pageCount}
                </span>
                <Button
                  variant="contained"
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: prev.pageIndex + 1,
                    }))
                  }
                  disabled={!table.getCanNextPage()}
                >
                  Suivant
                </Button>
              </div>
            </>
          )}
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          {/* Pagination haut */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Button disabled={modernPage === 0} onClick={() => setModernPage(p => Math.max(0, p - 1))}>Précédent</Button>
            <Typography>Page {modernPage + 1} / {totalModernPages}</Typography>
            <Button disabled={modernPage >= totalModernPages - 1} onClick={() => setModernPage(p => Math.min(totalModernPages - 1, p + 1))}>Suivant</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={`Actif${actifFilter === true ? ' (filtré)' : ''}`}
              color={actifFilter === true ? 'success' : 'default'}
              variant={actifFilter === true ? 'filled' : 'outlined'}
              onClick={() => setActifFilter(actifFilter === true ? null : true)}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
            <Chip
              label={`Non Actif${actifFilter === false ? ' (filtré)' : ''}`}
              color={actifFilter === false ? 'error' : 'default'}
              variant={actifFilter === false ? 'filled' : 'outlined'}
              onClick={() => setActifFilter(actifFilter === false ? null : false)}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
            {actifFilter !== null && (
              <Button size="small" onClick={() => setActifFilter(null)} sx={{ ml: 2 }}>Réinitialiser</Button>
            )}
            <TextField
              label="Recherche"
              variant="outlined"
              value={modernSearch}
              onChange={e => setModernSearch(e.target.value)}
              size="small"
              sx={{ ml: 2, width: 250 }}
            />
          </Box>
          {produitsPage.map(produit => (
            <Produit
              key={produit.id}
              produit={produit}
              achats={achatsByProduct[produit.id] || []}
              commandes={commandesByProduct[produit.id] || []}
              repricingRuleData={repricingRuleData}
              rules={rules}
              exchangeRates={exchangeRates}
              restrictionsByProduct={restrictionsByProduct}
              thresholds={thresholds}
              getProfit={getProfit}
              getMargin={getMargin}
              getROI={getROI}
              getTVADetails={getTVADetails}
              setSelectedProduct={setSelectedProduct}
              setOpenRepricerModal={setOpenRepricerModal}
              setAsinToRefresh={setAsinToRefresh}
              setOpenConfirmRefresh={setOpenConfirmRefresh}
              purgeRestrictionsCacheForAsin={purgeRestrictionsCacheForAsin}
              setRefreshRestrictionsTrigger={setRefreshRestrictionsTrigger}
              MARKETPLACES={MARKETPLACES}
              repricingTrends={repricingTrends}
              fournisseurs={fournisseurs}
              onEdit={handleEdit}
              onDelete={handleOpenDeleteDialog}
            />
          ))}
          {/* Pagination bas */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
            <Button disabled={modernPage === 0} onClick={() => setModernPage(p => Math.max(0, p - 1))}>Précédent</Button>
            <Typography>Page {modernPage + 1} / {totalModernPages}</Typography>
            <Button disabled={modernPage >= totalModernPages - 1} onClick={() => setModernPage(p => Math.min(totalModernPages - 1, p + 1))}>Suivant</Button>
          </Box>
        </Box>
      )}

      {/* Dialog pour ajouter/éditer un produit */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingId ? "Éditer un Produit" : "Ajouter un Produit"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Titre"
            fullWidth
            margin="dense"
            value={formData.Titre}
            onChange={(e) =>
              setFormData({ ...formData, Titre: e.target.value })
            }
          />
          <TextField
            label="EAN"
            fullWidth
            margin="dense"
            value={formData.EAN}
            onChange={(e) =>
              setFormData({ ...formData, EAN: e.target.value })
            }
          />
          <TextField
            label="ASIN"
            fullWidth
            margin="dense"
            value={formData.ASIN}
            onChange={(e) =>
              setFormData({ ...formData, ASIN: e.target.value })
            }
          />
          <TextField
            label="Stock"
            type="number"
            fullWidth
            margin="dense"
            value={formData.Stock}
            onChange={(e) =>
              setFormData({ ...formData, Stock: e.target.value })
            }
          />
          <TextField
            label="Prix de vente moyen"
            type="number"
            fullWidth
            margin="dense"
            value={formData["Prix de vente moyen"]}
            onChange={(e) =>
              setFormData({ ...formData, "Prix de vente moyen": e.target.value })
            }
          />
          <FormControlLabel
            control={<Switch checked={!!formData.Actif} onChange={e => setFormData({ ...formData, Actif: e.target.checked })} />}
            label="Actif"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingId ? "Mettre à jour" : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>

      

      {/* Dialog de confirmation pour la suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>Voulez-vous vraiment supprimer ce produit ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Non</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Oui
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale de gestion du repricer */}
      <DialogRepricer
        open={openRepricerModal}
        onClose={() => setOpenRepricerModal(false)}
        selectedProduct={selectedProduct}
        repricingRuleData={repricingRuleData}
        setRepricingRuleData={setRepricingRuleData}
        onRulesUpdated={refreshRepricingRules}
        restrictionsByProduct={restrictionsByProduct}
      />
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Dialog de confirmation pour le refresh des restrictions */}
      <Dialog open={openConfirmRefresh} onClose={() => setOpenConfirmRefresh(false)}>
        <DialogTitle>Rafraîchir les restrictions</DialogTitle>
        <DialogContent>
          <Typography>Voulez-vous vraiment rafraîchir les restrictions Amazon pour ce produit ? Cela va purger le cache et recharger les restrictions.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmRefresh(false)}>Annuler</Button>
          <Button
            onClick={() => {
              setOpenConfirmRefresh(false);
              if (asinToRefresh) {
                purgeRestrictionsCacheForAsin(asinToRefresh);
                setRefreshRestrictionsTrigger(t => t + 1);
                setSnackbar({ open: true, message: 'Restrictions rafraîchies avec succès', severity: 'success' });
                setAsinToRefresh(null);
              }
            }}
            variant="contained"
            color="primary"
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProduitsPage;
